import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EventInput } from '@fullcalendar/core';
import { take } from 'rxjs';
import {
  Item,
  SelectFieldComponent,
} from '../../form/select-field/select-field.component';
import { CalendarToolbarComponent } from '../calendar-toolbar/calendar-toolbar.component';
import {
  isAppuntamentoUnresolved as computeIsAppuntamentoUnresolved,
  convertUtcTimeToLocal,
  formatDateAwareTimeRange,
  formatPromemoriaDeadline,
  getEventZIndex,
  groupOverlappingEvents,
  isPromemoriaEvent,
} from '../calendar.utils';
import { QuickAddMenuComponent } from '../components/quick-add-menu.component';
import {
  MacchinarioDto,
  QuickAddMenuOption,
  ResourceSlotQuickAddEvent,
} from '../models/calendar.model';
import { HolidaysService } from '../services/holidays.service';
import { MachineryService } from '../services/machinery.service';
import {
  ParkedEvent,
  ParkingShelfComponent,
} from './parking-shelf/parking-shelf.component';
import {
  buildTimeSlots,
  computeEventHeight,
  computeEventTop,
  computeSlotTimeLabel,
  formatMinutesLabel,
  getDayBounds,
  minutesToDate,
  minutesToTopPx,
  pixelsToSnappedMinutes,
  SLOT_HEIGHT_PX,
  slotIndexToDate,
  START_HOUR,
} from './resource-day-calendar.utils';

export interface ResourceDayEvent extends EventInput {
  start: Date | string;
  end: Date | string;
  title: string;
  id: string;
}

@Component({
  selector: 'siat-resource-day-calendar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    CalendarToolbarComponent,
    SelectFieldComponent,
    ParkingShelfComponent,
    QuickAddMenuComponent,
  ],
  templateUrl: './resource-day-calendar.component.html',
  styleUrl: './resource-day-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceDayCalendarComponent {
  /**
   * The list of collaboratori.
   */
  readonly collaboratoriOptions = input<Item[]>([]);

  /**
   * Stores the list of operator event types
   */
  readonly operatorEventTypeItems = input<Item[]>([]);

  /**
   * Initial collaboratore.
   */
  readonly initialCollaboratore = input<Item | null>(null);

  /**
   *  A map of collaboratoreId -> events.
   */
  readonly eventsPerCollaboratore = input<Record<string, EventInput[]>>({});

  /**
   * Emit the id of the selected collaboratore
   */
  readonly collaboratoreSelected = output<string>();

  /**
   * Emit when the column selection changes
   */
  readonly columnSelectionsChange = output<(Item | null)[]>();

  /**
   * Emitted when an event is clicked
   */
  readonly eventClick = output<EventInput>();

  /**
   * Emitted when a slot is clicked
   */
  readonly slotClick = output<{
    date: Date;
    collaboratoreId: string | number;
  }>();

  /**
   * A flag it indicates whether to show the quick add menu or not.
   */
  readonly quickAddMenuEnabled = input<boolean>(false);

  /**
   * Emit when the quick add menu is opened.
   */
  readonly quickAddAction = output<ResourceSlotQuickAddEvent>();

  /**
   * Emitted when an event is dropped by the user.
   */
  readonly externalDrop = output<{
    activityId: string;
    date: Date;
    collaboratoreId: string | number;
  }>();

  /**
   * A flag it indicates whether to allow moving events or not.
   */
  readonly canMoveEvents = input<boolean>(false);

  /**
   * Emits the event move event.
   */
  readonly eventMove = output<{
    event: EventInput;
    sourceCollaboratoreId: string;
    targetCollaboratoreId: string;
    date: Date;
    /**
     * Il macchinario attivo nella colonna di destinazione, se presente —
     * distinto da `sourceCollaboratoreId`/`targetCollaboratoreId` perché lo
     * stesso ambulatorio complesso può occupare più colonne contemporaneamente
     * (una per macchinario, v. columnOptionsList): un drag tra due di quelle
     * colonne ha ID collaboratore identico ma deve comunque riassegnare
     * l'evento al macchinario della colonna di arrivo, non lasciarlo sul suo.
     */
    targetMacchinario?: Item | null;
    onPlaced?: () => void;
  }>();

  /**
   * Abilita il click sugli slot per creare nuovi eventi.
   */
  readonly slotClickEnabled = input<boolean>(false);

  /**
   * A flag it indicates whether to show the holidays or not.
   */
  readonly showHolidays = input<boolean>(false);

  /**
   * The city to use for holidays DEFAULT: TRENTO
   */
  readonly holidayCitta = input<string>('TRENTO');

  /**
   * If true: block holiday insert
   */
  readonly blockHolidayInsert = input<boolean>(false);

  /**
   * Template for the extra header.
   */
  readonly extraHeaderTemplate = input<TemplateRef<unknown>>();

  /**
   * A flag it indicates whether to show the toolbar or not.
   */
  readonly showToolbar = input<boolean>(true);

  /**
   * Options for the add-event button (e.g. ['Assenza'] or ['Nuovo turno']).
   */
  readonly splitButtonOptions = input<string[]>([]);

  /**
   * Emitted when the split button is clicked
   */
  readonly splitButtonAction = output<string>();

  /**
   * A predicate identifying "reschedulable" events, used to style them differently.
   */
  readonly canRescheduleEvent = input<(event: EventInput) => boolean>(
    () => false
  );

  /**
   * A predicate identifying events that can only be moved within their own column.
   */
  readonly isColumnLockedEvent = input<(event: EventInput) => boolean>(
    () => false
  );

  /**
   * A predicate identifying "assenza" events, used to style them differently.
   */
  readonly isAssenzaEvent = input<(event: EventInput) => boolean>(() => false);

  /**
   * A predicate identifying "turno" events, used to style them differently.
   */
  readonly isTurnoEvent = input<(event: EventInput) => boolean>(() => false);

  /**
   * A predicate identifying "travel time" events, used to block slot clicks
   * that would otherwise open the "add new event" flow on top of them.
   */
  readonly isTravelEvent = input<(event: EventInput) => boolean>(() => false);

  /**
   * The holidays service
   */
  private readonly holidaysService = inject(HolidaysService);

  /**
   * Shared machinery (macchinario) logic.
   */
  private readonly machineryService = inject(MachineryService);

  /**
   * Injector, used to schedule post-render callbacks outside the constructor.
   */
  private readonly injector = inject(Injector);

  /**
   * The current date
   */
  protected readonly currentDate = signal<Date>(new Date());

  /**
   *  True if the current day is a holiday
   */
  protected readonly isCurrentDayHoliday = computed(() =>
    this.holidaysService.isHoliday(this.currentDate())
  );

  /**
   * True if the current day is a weekend
   */
  protected readonly isWeekend = computed(() => {
    const day = this.currentDate().getDay();
    return day === 0 || day === 6;
  });

  /**
   * The current time
   */
  protected readonly now = signal<Date>(new Date());

  /**
   * True if the current day is today
   */
  protected readonly isToday = computed(() => {
    const today = this.now();
    const shown = this.currentDate();
    return (
      shown.getFullYear() === today.getFullYear() &&
      shown.getMonth() === today.getMonth() &&
      shown.getDate() === today.getDate()
    );
  });

  /**
   * Top offset for the "adesso" line
   */
  protected readonly nowTop = computed(() => {
    const now = this.now();
    const minutesFromStart =
      (now.getHours() - START_HOUR) * 60 + now.getMinutes();
    return Math.max(0, (minutesFromStart / 30) * SLOT_HEIGHT_PX);
  });

  /**
   * Formatted date label
   */
  readonly formattedDate = computed(() =>
    this.currentDate().toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  );

  /**
   * Columns selections
   */
  protected readonly columnSelections = signal<(Item | null)[]>([null, null]);

  /**
   * Index of columns actually selected
   */
  protected readonly columnIndices = computed(() =>
    this.columnSelections().map((_, i) => i)
  );

  /**
   * Maximum number of columns allowed
   */
  protected readonly maxColumns = computed(
    () => this.collaboratoriOptions().length
  );

  /**
   * A flag indicating whether the "add column" button should be enabled
   */
  protected readonly canAddColumn = computed(
    () => this.columnSelections().length < this.maxColumns()
  );

  /**
   * A flag indicating whether the "remove column" button should be enabled
   */
  protected readonly canRemoveColumn = computed(
    () => this.columnSelections().length > 1
  );

  /**
   * Columns options available for selection
   */
  protected readonly columnOptionsList = computed(() => {
    const selections = this.columnSelections();
    const allOptions = this.collaboratoriOptions();
    return selections.map((_, columnIndex) => {
      const selectedIds = new Set(
        selections
          .filter((_, i) => i !== columnIndex)
          .filter(Boolean)
          // Un ambulatorio complesso (con macchinari) resta selezionabile
          // anche in un'altra colonna già occupata dallo stesso: serve poter
          // vedere due macchinari dello stesso ambulatorio affiancati, per
          // spostare un evento dall'uno all'altro via drag cross-colonna. Un
          // sanitario (o un ambulatorio senza macchinari) resta invece
          // escluso altrove, non ha senso vederlo due volte.
          .filter(s => !(s?.macchinari?.length > 0))
          .map(s => s?.id)
      );
      return allOptions.filter(o => !selectedIds.has(o.id));
    });
  });

  /*
   * Time slots for the grid
   */
  protected readonly timeSlots = buildTimeSlots();

  /**
   * The grid height in px
   */
  protected readonly gridHeight = this.timeSlots.length * SLOT_HEIGHT_PX;

  /**
   * The slot height in px
   */
  protected readonly slotHeight = SLOT_HEIGHT_PX;

  /**
   * Viewchild for column drag and drop
   */
  private readonly bodyEl = viewChild<ElementRef<HTMLElement>>('rdcBody');

  /**
   * Column form groups
   */
  protected columnFormGroups: FormGroup[] = [];

  /**
   *  Column machinery selections
   */
  protected readonly columnMachinerySelections = signal<(Item | null)[]>([
    null,
    null,
  ]);

  /** Un FormGroup per ogni colonna, con un controllo `macchinario`. */
  protected columnMachineryFormGroups: FormGroup[] = [];

  /**
   * Machinery options available for a column.
   */
  protected readonly columnMachineryOptionsList = computed(() =>
    this.columnSelections().map(item =>
      this.machineryService.mapToItems(
        item?.macchinari as MacchinarioDto[] | undefined
      )
    )
  );

  /**
   * The current drag state
   */
  protected readonly dragState = signal<{
    columnIndex: number;
    slotIndex: number;
    snappedMinutes: number;
    isValid: boolean;
    isColumnLocked?: boolean;
  } | null>(null, {
    equal: (a, b) => {
      if (a === null && b === null) return true;
      if (a === null || b === null) return false;
      return (
        a.columnIndex === b.columnIndex &&
        a.slotIndex === b.slotIndex &&
        a.snappedMinutes === b.snappedMinutes &&
        a.isValid === b.isValid &&
        a.isColumnLocked === b.isColumnLocked
      );
    },
  });

  /**
   * The number of placeholder duration slots
   */
  protected readonly PLACEHOLDER_DURATION_SLOTS = 2;

  /**
   * The currently dragged event.
   */
  private draggedEvent: ParkedEvent | null = null;

  /**
   * Quick add menu
   */
  private readonly quickAddMenu = viewChild(QuickAddMenuComponent);
  protected readonly quickAddMenuOptions = signal<QuickAddMenuOption[]>([]);
  private pendingQuickAddSlot: {
    date: Date;
    collaboratoreId: string | number;
    columnIndex: number;
  } | null = null;

  constructor() {
    this.columnFormGroups.push(this.createColumnFormGroup(null));
    this.columnFormGroups.push(this.createColumnFormGroup(null));
    this.columnMachineryFormGroups.push(
      this.createColumnMachineryFormGroup(null)
    );
    this.columnMachineryFormGroups.push(
      this.createColumnMachineryFormGroup(null)
    );

    const nowIntervalId = setInterval(() => this.now.set(new Date()), 60000);

    inject(DestroyRef).onDestroy(() => clearInterval(nowIntervalId));

    afterNextRender(() => {
      const el = this.bodyEl()?.nativeElement;
      if (el) el.scrollTop = 7 * 2 * SLOT_HEIGHT_PX;
    });
  }

  /**
   * Auto-selects the first collaboratore into column 0 once options arrive.
   */
  private readonly autoSelectFirstCollaboratore = effect(() => {
    const options = this.collaboratoriOptions();
    if (options.length > 0) {
      const current = this.columnSelections();
      if (current[0] === null) {
        const initial = this.initialCollaboratore();
        const selected = initial
          ? (options.find(o => o.id === initial.id) ?? options[0])
          : options[0];
        this.columnFormGroups[0]
          ?.get('operatore')
          ?.setValue(selected, { emitEvent: false });
        this.columnSelections.update(prev =>
          prev.map((c, i) => (i === 0 ? selected : c))
        );
        this.collaboratoreSelected.emit(String(selected.id));
      }
    }
  });

  /**
   * Re-emits `columnSelectionsChange` whenever the column selections change.
   */
  private readonly emitColumnSelectionsChange = effect(() => {
    this.columnSelectionsChange.emit(this.columnSelections());
  });

  /**
   * Revalidates/resets each column's selected machinery when the available
   * options for that column change.
   */
  private readonly syncColumnMachinerySelections = effect(() => {
    const optionsList = this.columnMachineryOptionsList();
    const current = this.columnMachinerySelections();
    optionsList.forEach((options, i) => {
      const currentValue = current[i] ?? null;
      const stillValid =
        currentValue &&
        options.some(o => String(o.id) === String(currentValue.id));
      const next =
        options.length === 0 ? null : stillValid ? currentValue : options[0];
      if (next === currentValue) return;
      this.columnMachineryFormGroups[i]
        ?.get('macchinario')
        ?.setValue(next, { emitEvent: false });
      this.columnMachinerySelections.update(prev =>
        prev.map((c, idx) => (idx === i ? next : c))
      );
    });
  });

  /**
   * Keeps the holidays service's insert-block flag in sync with the input.
   */
  private readonly syncHolidayInsertBlock = effect(() => {
    this.holidaysService.blockHolidayInsert.set(this.blockHolidayInsert());
  });

  /**
   * Fetches the current year's holidays the first time they're needed.
   */
  private readonly loadHolidaysForCurrentYear = effect(() => {
    if (!this.showHolidays()) return;
    const year = this.currentDate().getFullYear();
    if (this.holidaysService.isYearLoaded(year)) return;
    this.holidaysService.markYearLoaded(year);
    this.holidaysService
      .getHolidays(year, this.holidayCitta())
      .pipe(take(1))
      .subscribe(dates => this.holidaysService.addHolidays(dates));
  });

  /**
   * Add a new column
   */
  protected addColumn(): void {
    const fg = this.createColumnFormGroup(null);
    this.columnFormGroups.push(fg);
    this.columnSelections.update(prev => [...prev, null]);
    this.columnMachineryFormGroups.push(
      this.createColumnMachineryFormGroup(null)
    );
    this.columnMachinerySelections.update(prev => [...prev, null]);
  }

  protected removeColumn(index: number): void {
    this.columnFormGroups.splice(index, 1);
    this.columnSelections.update(prev => prev.filter((_, i) => i !== index));
    this.columnMachineryFormGroups.splice(index, 1);
    this.columnMachinerySelections.update(prev =>
      prev.filter((_, i) => i !== index)
    );
  }

  /**
   * On calendar navigation.
   */
  navigate(direction: 'prev' | 'next' | 'today'): void {
    if (direction === 'today') {
      this.currentDate.set(new Date());
      return;
    }

    const delta = direction === 'prev' ? -1 : 1;
    const d = new Date(this.currentDate());
    d.setDate(d.getDate() + delta);
    this.currentDate.set(d);
  }

  /**
   * Jump to a specific date, centering the scroller on that time so the
   * target slot lands in the middle of the visible grid.
   */
  jumpToDate(date: Date | null): void {
    if (!date) return;
    this.currentDate.set(date);

    const el = this.bodyEl()?.nativeElement;
    if (!el) return;
    const minutesFromMidnight = date.getHours() * 60 + date.getMinutes();
    const slotTop = minutesToTopPx(minutesFromMidnight);
    el.scrollTop = slotTop - el.clientHeight / 2 + SLOT_HEIGHT_PX / 2;
  }

  /**
   * On column select
   */
  protected onColumnSelect(columnIndex: number, item: Item | null): void {
    this.columnSelections.update(prev => {
      const next = [...prev];
      next[columnIndex] = item;
      return next;
    });
    if (item) {
      this.collaboratoreSelected.emit(String(item.id));
    }
  }

  /**
   * Get column selection
   */
  protected getColumnSelection(index: number): Item | null {
    return this.columnSelections()[index];
  }

  /**
   * On slot click.
   */
  protected onSlotClick(
    columnIndex: number,
    slotIndex: number,
    event?: Event
  ): void {
    if (!this.slotClickEnabled()) return;
    if (!this.isSlotAvailable(columnIndex, slotIndex)) return;
    const item = this.columnSelections()[columnIndex];
    if (!item) return;

    let date = slotIndexToDate(slotIndex, this.currentDate());
    const macchinario = this.getColumnMachinery(columnIndex);

    if (macchinario) {
      const snapped = this.machineryService.snapToSlot(date, macchinario);
      if (!snapped || !this.hasNoMacchinarioConflict(columnIndex, snapped)) {
        return;
      }
      date = snapped;
    }

    this.slotClick.emit({ date, collaboratoreId: item.id });

    if (this.quickAddMenuEnabled()) {
      this.openQuickAddMenu(item, date, columnIndex, event);
    }
  }

  /**
   * Open quick add menu
   */
  private openQuickAddMenu(
    item: Item,
    date: Date,
    columnIndex: number,
    event?: Event
  ): void {
    const anchor =
      event instanceof MouseEvent
        ? { x: event.clientX, y: event.clientY }
        : (() => {
            const rect = (
              event?.target as HTMLElement | undefined
            )?.getBoundingClientRect();
            return rect ? { x: rect.left, y: rect.top } : { x: 0, y: 0 };
          })();

    this.pendingQuickAddSlot = { date, collaboratoreId: item.id, columnIndex };
    this.quickAddMenuOptions.set(this.buildQuickAddOptions(item));
    this.quickAddMenu()?.open(anchor);
  }

  /**
   * Quick-add options for `item`'s column: an ambulatorio (simple or
   * complex) only ever offers "Nuovo turno"; a sanitario column offers the
   * `operatorEventTypeItems` typology (or the hardcoded trio as a fallback).
   */
  private buildQuickAddOptions(item: Item): QuickAddMenuOption[] {
    if (item.isAmbulatorio) {
      return [{ action: 'Nuovo turno', icon: '🏢', label: 'Nuovo Turno' }];
    }
    const items = this.operatorEventTypeItems();
    if (!items.length) {
      return [
        { action: 'Assenza', icon: '🚫', label: 'Assenza' },
        { action: 'Riunione', icon: '👥', label: 'Riunione' },
        { action: 'Promemoria', icon: '🔔', label: 'Promemoria' },
      ];
    }
    return items.map(typeItem => ({
      action: typeItem.name,
      icon: typeItem.icon ?? '📌',
      label: typeItem.name,
    }));
  }

  /**
   * Handle quick add option click
   */
  protected onQuickAddOptionClick(action: string): void {
    const slot = this.pendingQuickAddSlot;
    this.pendingQuickAddSlot = null;
    if (!slot) return;
    const macchinario = this.getColumnMachinery(slot.columnIndex);
    const durationMs = (macchinario?.durataSlotMacchinario ?? 30) * 60000;
    const end = new Date(slot.date.getTime() + durationMs);
    this.quickAddAction.emit({
      action,
      start: slot.date,
      end,
      collaboratoreId: slot.collaboratoreId,
    });
  }

  /**
   * On document drag end.
   */
  @HostListener('document:dragend')
  protected onDocumentDragEnd(): void {
    this.dragState.set(null);
    this.draggedEvent = null;
  }

  /**
   * On event drag start.
   */
  protected onEventDragStart(
    dragEvent: DragEvent,
    event: EventInput,
    sourceCollaboratoreId: string | number
  ): void {
    this.draggedEvent = {
      event,
      sourceCollaboratoreId: String(sourceCollaboratoreId),
    };
    dragEvent.dataTransfer?.setData('eventId', String(event.id));
  }

  /**
   * On column drag over.
   * @param event
   * @param columnIndex
   */
  protected onColumnDragOver(event: DragEvent, columnIndex: number): void {
    if (!this.getColumnSelection(columnIndex)) return;

    const colEl = event.currentTarget as HTMLElement;
    const rect = colEl.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const macchinario = this.getColumnMachinery(columnIndex);
    const machinerySnapped = this.machineryService.snapPixelsToSlot(
      relativeY,
      macchinario
    );
    const snappedMinutes =
      machinerySnapped ?? pixelsToSnappedMinutes(relativeY);
    const slotIndex = Math.max(
      0,
      Math.min(Math.floor(snappedMinutes / 30), this.timeSlots.length - 1)
    );

    const excludeEventId = this.draggedEvent?.event?.id;
    const isColumnLocked =
      !!this.draggedEvent &&
      this.isColumnLockedEvent()(this.draggedEvent.event) &&
      this.draggedEvent.sourceCollaboratoreId !==
        String(this.getColumnSelection(columnIndex)?.id);
    const isValid =
      !isColumnLocked &&
      (!macchinario || machinerySnapped !== null) &&
      this.isSlotAvailable(columnIndex, slotIndex, excludeEventId) &&
      this.hasNoMacchinarioConflict(
        columnIndex,
        minutesToDate(snappedMinutes, this.currentDate()),
        excludeEventId
      );

    if (isValid) event.preventDefault();

    this.dragState.set({
      columnIndex,
      slotIndex,
      snappedMinutes,
      isValid,
      isColumnLocked,
    });
  }

  /**
   * Has no macchinario conflict.
   */
  private hasNoMacchinarioConflict(
    columnIndex: number,
    start: Date,
    excludeEventId?: string | number
  ): boolean {
    const macchinario = this.getColumnMachinery(columnIndex);
    if (!macchinario) return true;
    const durationMs = (macchinario.durataSlotMacchinario ?? 30) * 60000;
    const end = new Date(start.getTime() + durationMs);
    return !this.machineryService.hasBookingConflict(
      macchinario.id,
      start,
      end,
      this.getEventsForColumn(columnIndex),
      excludeEventId
    );
  }

  /**
   * On column drop.
   * @param event
   * @param columnIndex
   */
  protected onColumnDrop(event: DragEvent, columnIndex: number): void {
    event.preventDefault();

    const state = this.dragState();
    this.dragState.set(null);

    const item = this.columnSelections()[columnIndex];
    if (!item) return;
    if (!state || !state.isValid || state.columnIndex !== columnIndex) return;

    const date = minutesToDate(state.snappedMinutes, this.currentDate());

    const eventId = event.dataTransfer?.getData('eventId');
    if (eventId) {
      const dragged = this.draggedEvent;
      this.draggedEvent = null;
      if (!dragged || String(dragged.event.id) !== eventId) return;

      const isSameColumn = dragged.sourceCollaboratoreId === String(item.id);
      if (!isSameColumn && !this.canMoveEvents()) return;
      const isParkedEvent = this.parkedEvents().some(
        p => String(p.event.id) === String(dragged.event.id)
      );

      this.eventMove.emit({
        event: dragged.event,
        sourceCollaboratoreId: dragged.sourceCollaboratoreId,
        targetCollaboratoreId: String(item.id),
        date,
        targetMacchinario: this.getColumnMachinery(columnIndex),
        onPlaced: isParkedEvent
          ? () => this.clearParkedEvent(dragged.event.id)
          : undefined,
      });
      return;
    }

    const activityId = event.dataTransfer?.getData('activityId');
    if (!activityId) return;

    this.externalDrop.emit({ activityId, date, collaboratoreId: item.id });
  }

  /**
   * On columng drag leave.
   * @param event
   * @param columnIndex
   */
  protected onColumnDragLeave(event: DragEvent, columnIndex: number): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const colEl = event.currentTarget as HTMLElement;
    if (!relatedTarget || !colEl.contains(relatedTarget)) {
      if (this.dragState()?.columnIndex === columnIndex) {
        this.dragState.set(null);
      }
    }
  }

  /**
   * The height of a promemoria event.
   */
  protected readonly PROMEMORIA_HEIGHT_PX = 24;

  /**
   * Get events for a column.
   */
  protected getEventsForColumn(columnIndex: number): EventInput[] {
    const item = this.columnSelections()[columnIndex];
    if (!item) return [];
    const map = this.eventsPerCollaboratore();
    const events = (map[String(item.id)] ?? []).filter(e => !e.allDay);
    const parkedIds = new Set(
      this.parkedEvents()
        .filter(p => p.sourceCollaboratoreId === String(item.id))
        .map(p => String(p.event.id))
    );
    const withoutParked = events.filter(e => !parkedIds.has(String(e.id)));
    const machinery = this.getColumnMachinery(columnIndex);
    const withoutOtherMachinery = withoutParked.filter(e => {
      const idMacchinario = e.extendedProps?.['idMacchinario'];
      return (
        !machinery ||
        !this.machineryService.hasValidId(idMacchinario) ||
        String(idMacchinario) === String(machinery.id)
      );
    });
    const { start: dayStart, end: dayEnd } = getDayBounds(this.currentDate());
    const eventsInDay = withoutOtherMachinery.filter(e => {
      const eventStart = new Date(e.start as string);
      const eventEnd = new Date(e.end as string);
      return eventStart < dayEnd && eventEnd > dayStart;
    });

    const assenzaEvents = eventsInDay.filter(e => this.isAssenzaEvent()(e));
    const otherEvents = eventsInDay.filter(e => !this.isAssenzaEvent()(e));

    return [...assenzaEvents, ...groupOverlappingEvents(otherEvents)];
  }

  /**
   * Event's vertical (top) position in pixels, clamped to the start of the current day.
   * @return {number}
   */
  protected getEventTop(event: EventInput): number {
    return computeEventTop(event, getDayBounds(this.currentDate()).start);
  }

  /**
   * Event's height in pixels, clamped to the end of the
   * current day — fixed and duration-independent for Promemoria, which
   * otherwise occupy the grid in proportion to a long, low-signal duration.
   * @return {number} an
   */
  protected getEventHeight(event: EventInput): number {
    if (isPromemoriaEvent(event)) return this.PROMEMORIA_HEIGHT_PX;
    const { start, end } = getDayBounds(this.currentDate());
    return computeEventHeight(event, start, end);
  }

  /**
   * Event's left position in pixels, clamped to the start of the current day.
   * @return {string}
   */
  protected getEventLeftCss(event: EventInput): string {
    const laneIndex = (event.extendedProps?.['laneIndex'] as number) ?? 0;
    const laneCount = (event.extendedProps?.['laneCount'] as number) ?? 1;
    if (laneCount <= 1) return '3px';
    return `calc(3px + (100% - 6px) * ${laneIndex} / ${laneCount})`;
  }

  /**
   * CSS `width` matching `getEventLeftCss`'s division, with
   * a small 2px gap so adjacent sub-columns don't visually merge into one
   * block.
   * @return {string}
   */
  protected getEventWidthCss(event: EventInput): string {
    const laneCount = (event.extendedProps?.['laneCount'] as number) ?? 1;
    if (laneCount <= 1) return 'calc(100% - 6px)';
    return `calc((100% - 6px) / ${laneCount} - 2px)`;
  }

  /**
   * Event's z-index
   * @return {number}
   */
  protected getEventZIndex(event: EventInput): number {
    return getEventZIndex(this.isAssenzaEvent()(event));
  }

  /**
   * True if `event` is the synthetic "+ N altri" overflow event
   * @return {boolean}
   */
  protected isClusterOverflowEvent(event: EventInput): boolean {
    return !!event.extendedProps?.['isClusterOverflow'];
  }

  /**
   * Get the top (px) for the overflow badge strip.
   * @return {number}
   */
  protected getOverflowTop(event: EventInput): number {
    return this.getEventTop(event) + this.getEventHeight(event) + 2;
  }

  /**
   * True if `event` is a "promemoria"
   * @return {boolean}
   */
  protected isPromemoriaCard(event: EventInput): boolean {
    return isPromemoriaEvent(event);
  }

  /**
   * True if `event` is a past appuntamento with no outcome recorded yet.
   * @return {boolean}
   */
  protected isAppuntamentoUnresolved(event: EventInput): boolean {
    return computeIsAppuntamentoUnresolved(event);
  }

  /** @return {string} compact "Scade" label for a promemoria's end (see `formatPromemoriaDeadline`). */
  protected getPromemoriaDeadlineLabel(event: EventInput): string {
    return formatPromemoriaDeadline(
      new Date(event.start as string),
      new Date(event.end as string)
    );
  }

  /** @return {boolean} true if the column's selected resource is an "ambulatorio" (vs. a "sanitario"). */
  protected isAmbulatorioColumn(columnIndex: number): boolean {
    return !!this.getColumnSelection(columnIndex)?.isAmbulatorio;
  }

  /**
   * True if this column has machinery to pick from.
   * @return {boolean}
   */
  protected columnHasMachinery(columnIndex: number): boolean {
    return this.columnMachineryOptionsList()[columnIndex]?.length > 0;
  }

  /**
   * Get the machinery currently active for this column.
   *  @return {Item | null}
   */
  protected getColumnMachinery(columnIndex: number): Item | null {
    return this.columnMachinerySelections()[columnIndex] ?? null;
  }

  /**
   * True if `slotIndex` is within the active machinery's operating hours.
   * @return {boolean}
   */
  protected isSlotWithinMachineryHours(
    columnIndex: number,
    slotIndex: number
  ): boolean {
    const orari = this.getColumnMachinery(columnIndex)?.orariAttivita;
    if (!orari?.length) return true;
    const slotLabel = computeSlotTimeLabel(slotIndex);
    return orari.some(o => {
      const start = convertUtcTimeToLocal(o.ora_inizio).slice(0, 5);
      const end = convertUtcTimeToLocal(o.ora_fine).slice(0, 5);
      return slotLabel >= start && slotLabel < end;
    });
  }

  /**
   * The events currently parked on the parking shelf.
   */
  protected readonly parkedEvents = signal<ParkedEvent[]>([]);

  /**
   * Called when an event is dropped onto the parking shelf.
   */
  protected onParkingDrop(dragEvent: DragEvent): void {
    this.dragState.set(null);

    const eventId = dragEvent.dataTransfer?.getData('eventId');
    const dragged = this.draggedEvent;
    this.draggedEvent = null;
    if (!eventId || !dragged || String(dragged.event.id) !== eventId) return;

    this.parkedEvents.update(list => [
      ...list.filter(p => String(p.event.id) !== eventId),
      dragged,
    ]);
  }

  /**
   * On parked event drag start.
   */
  protected onParkedEventDragStart(
    dragEvent: DragEvent,
    parked: ParkedEvent
  ): void {
    this.draggedEvent = parked;
    dragEvent.dataTransfer?.setData('eventId', String(parked.event.id));
  }

  /**
   * Clears the event from the parking shelf.
   */
  protected clearParkedEvent(eventId: string | number): void {
    this.parkedEvents.update(list =>
      list.filter(p => String(p.event.id) !== String(eventId))
    );
  }

  /**
   * Get the time label for an event.
   * @return {string}
   */
  protected getEventTimeLabel(event: EventInput): string {
    return formatDateAwareTimeRange(
      new Date(event.start as string),
      new Date(event.end as string)
    );
  }

  /**
   * Get the title for an event.
   */
  protected getEventCardTitle(event: EventInput): string {
    if (this.isTurnoEvent()(event)) {
      return event.extendedProps?.['descrizione'] || event.title;
    }
    return event.title;
  }

  /**
   * The placeholder top offset (px) for the drag's snapped minute (v. dragState).
   * @return {number}
   */
  protected getPlaceholderTopPx(snappedMinutes: number): number {
    return minutesToTopPx(snappedMinutes);
  }

  /**
   * The time label (e.g. "13:45") for the drag's snapped minute (v. dragState).
   *  @return {string}
   * */
  protected getSnappedTimeLabel(snappedMinutes: number): string {
    return formatMinutesLabel(snappedMinutes);
  }

  /**
   * Check if a slot is valid for a new event:
   */
  private isSlotDateValid(slotIndex: number): boolean {
    const slotDate = slotIndexToDate(slotIndex, this.currentDate());
    if (slotDate < new Date(Date.now() - 60000)) return false;
    if (this.holidaysService.blockHolidayInsert() && this.isCurrentDayHoliday())
      return false;
    return true;
  }

  /**
   * Check if a slot is available for a new event, ignoring the dragged event itself.
   * @return {boolean}
   */
  protected isSlotAvailable(
    columnIndex: number,
    slotIndex: number,
    excludeEventId?: string | number
  ): boolean {
    if (!this.isSlotDateValid(slotIndex)) return false;
    const slotStart = slotIndexToDate(slotIndex, this.currentDate());
    const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);
    return !this.getEventsForColumn(columnIndex).some(
      e =>
        String(e.id) !== String(excludeEventId) &&
        (this.isAssenzaEvent()(e) || this.isTravelEvent()(e)) &&
        new Date(e.start as string) < slotEnd &&
        new Date(e.end as string) > slotStart
    );
  }

  /**
   * Create a FormGroup with a control `operatore` and sottoscrive le
   */
  private createColumnFormGroup(initialValue: Item | null): FormGroup {
    const fg = new FormGroup({
      operatore: new FormControl<Item | null>(initialValue),
    });

    fg.get('operatore').valueChanges.subscribe(item => {
      if (typeof item !== 'object') return;
      const idx = this.columnFormGroups.indexOf(fg);
      if (idx === -1) return;
      this.onColumnSelect(idx, item as Item | null);
    });
    return fg;
  }

  /**
   * Create a FormGroup with a control `macchinario` and sottoscrive le
   */
  private createColumnMachineryFormGroup(initialValue: Item | null): FormGroup {
    const fg = new FormGroup({
      macchinario: new FormControl<Item | null>(initialValue),
    });
    fg.get('macchinario').valueChanges.subscribe(item => {
      if (typeof item !== 'object') return;
      const idx = this.columnMachineryFormGroups.indexOf(fg);
      if (idx === -1) return;
      this.columnMachinerySelections.update(prev =>
        prev.map((c, i) => (i === idx ? (item as Item | null) : c))
      );
    });
    return fg;
  }
}
